import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Modal from "../components/Modal";
import Card from "../components/Card";
import Button from "../components/Buttons";
import Input from "../components/Input";
import LoadingSpinner from "../components/LoadingSpinner";
import { Plus, FileText, Shield, Trash2, RefreshCw, Users, Eye } from "lucide-react";
import { severityPostureScore } from '../utils/riskScoringEngine';
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import OrgAssignAssessModal from "../components/OrgAssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import { useRBAC } from "../hooks/useRBAC";
import AssessmentActivityLog from "../components/AssessmentActivityLog";

const RansomwareOrganizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [respondentsAssessment, setRespondentsAssessment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const regions = useRegions();
  const { verticals } = useVerticals();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canViewOrganizationAssessments, canAssessViaAssignment, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const selectedOrgData = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();

  // Build the rolled-up card fields from a /RansomwareOrganization/{id} payload. The backend already
  // rolls the assessment risks up to ACTIVE severity counts (excluding fully-remediated / closed), so
  // there's no per-risk work here.
  const buildOrgPatch = (org) => {
    const latestAssessment = org.latestAssessment || null;
    if (latestAssessment && latestAssessment.riskCounts) {
      const rc = latestAssessment.riskCounts;
      const riskCounts = {
        CRITICAL: rc.critical || 0,
        HIGH: rc.high || 0,
        MEDIUM: rc.medium || 0,
        LOW: rc.low || 0,
      };
      return {
        latestAssessment,
        riskCounts,
        totalRisks: org.totalRisks ?? latestAssessment.riskCount ?? 0,
        activeRisks: org.activeRisks,
        riskScore: severityPostureScore(riskCounts),
        hasAssessment: true,
      };
    }
    // No assessment, but there may be tagged org risks (e.g. Enzoic) — keep the backend's tag-aware
    // total so the "View Risks" button can surface them.
    return { totalRisks: org.totalRisks ?? 0, activeRisks: org.activeRisks, hasAssessment: false };
  };

  const fetchOrgAssessmentData = async (orgId) => {
    const org = await apiCall("GET", `/RansomwareOrganization/${orgId}`);
    return buildOrgPatch(org);
  };

  // fetch organizations for tenant, then their assessment rollups — resolve everything BEFORE the
  // first paint so the page shows the spinner until it can render final data (no zeroed-then-refresh
  // flicker), matching /RansomwareDeals.
  const fetchOrganizations = async () => {
    try {
      const tenantId = user?.tenantId || 0;
      if (!tenantId) {
        setOrganizations([]);
        return;
      }
      const data = await apiCall("GET", `/RansomwareOrganization/details`);
      const baseList = (Array.isArray(data) ? data : []).map((org) => ({
        ...org,
        hasAssessment: false,
        latestAssessment: null,
        totalRisks: 0,
        riskScore: 0,
        riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
      }));
      const enriched = await Promise.all(
        baseList.map(async (o) => {
          try {
            return { ...o, ...(await fetchOrgAssessmentData(o.organizationId)) };
          } catch {
            return o;
          }
        })
      );
      setOrganizations(enriched);
    } catch (err) {} finally {
      setLoading(false);
    }
  };

  // Single-org refresh (e.g. after (re)assigning) — updates just that row in place.
  const refreshOrgAssessment = async (orgId) => {
    try {
      const patch = await fetchOrgAssessmentData(orgId);
      setOrganizations((prev) => prev.map((o) => (o.organizationId === orgId ? { ...o, ...patch } : o)));
    } catch (err) {}
  };

  useEffect(() => {
    fetchOrganizations();
  }, [user]);

  // auto-select from navigation state
  useEffect(() => {
    const autoSelectId = location.state?.selectedOrgID;
    if (autoSelectId && organizations.length > 0) {
      const target = organizations.find((o) => o.organizationId === autoSelectId);
      if (target) setSelectedOrg(target);
    }
  }, [organizations, location.state]);

  const calculateRiskScore = (riskCounts) => {
    return severityPostureScore(riskCounts);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/ransomwareAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchOrganizations();
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
      await apiCall('POST', `/ransomwareAssessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      navigate(`/RansomwareAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
      setReassessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Ransomware Organization Assessment</h1>
          <p className="mt-2 text-gray-600">Manage Ransomware organization risk assessment</p>
        </div>

        <Card>
          <div className="space-y-3">
            {organizations.map((org) => (
              <div
                key={org.organizationId}
                className="p-4 border-2 rounded-lg transition-colors border-gray-200 hover:border-gray-300"
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-content-primary">{org.organizationName}<AssessmentStatusBadge status={org.latestAssessment?.ransomwareStatus} /></h3>
                        {org.latestAssessment?.ransomwareAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{org.latestAssessment.ransomwareAssignedToUserName}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Risk details */}
                    {org.hasAssessment ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {org.riskCounts?.CRITICAL > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{org.riskCounts.CRITICAL} Critical</span>
                          )}
                          {org.riskCounts?.HIGH > 0 && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{org.riskCounts.HIGH} High</span>
                          )}
                          {org.riskCounts?.MEDIUM > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{org.riskCounts.MEDIUM} Medium</span>
                          )}
                          {org.riskCounts?.LOW > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{org.riskCounts.LOW} Low</span>
                          )}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Risk Score: {org.riskScore || 0}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${org.riskScore <= 40 ? "bg-green-500" : org.riskScore <= 60 ? "bg-yellow-500" : org.riskScore <= 80 ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${org.riskScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No Assessment Data</div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-3">
                  {canManageRisks && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={org.hasAssessment && org.latestAssessment?.ransomwareStatus === "completed"}
                      title={org.hasAssessment && org.latestAssessment?.ransomwareStatus === "completed" ? "Assessment is completed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntityForAssign(org);
                        setShowAssignModal(true);
                      }}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {org.latestAssessment?.ransomwareAssignedToUserId ? "Reassign" : "Assign"}
                    </Button>
                  )}

                  {canManageRisks && org.latestAssessment?.ransomwareAssessmentID && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Manage additional respondents for this assessment"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRespondentsAssessment({
                          id: org.latestAssessment.ransomwareAssessmentID,
                          organizationId: org.organizationId,
                        });
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Respondents
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={
                      !canAssess(org.latestAssessment?.ransomwareAssignedToUserId, null, null, null, org.latestAssessment?.assigneeUserIds || []) ||
                      (org.hasAssessment && (org.latestAssessment?.ransomwareStatus === "completed" || org.latestAssessment?.ransomwareStatus === "analyzing"))
                    }
                    title={
                      (org.hasAssessment && org.latestAssessment?.ransomwareStatus === "completed" ? "Assessment is completed"
                        : org.hasAssessment && org.latestAssessment?.ransomwareStatus === "analyzing" ? "Assessment is being analyzed"
                        : "")
                        || getAssessDisabledReason(org.latestAssessment?.ransomwareAssignedToUserId, null, null, null, org.latestAssessment?.assigneeUserIds || [])
                        || ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!org.latestAssessment?.ransomwareAssignedToUserId) {
                        setShowUnassignedAlert(true);
                        return;
                      }
                      const ransomPath = org.latestAssessment?.ransomwareAssessmentID
                        ? `/RansomwareAssessment/${org.latestAssessment.ransomwareAssessmentID}`
                        : "/RansomwareAssessment";
                      navigate(ransomPath, {
                        state: {
                          organizationId: org.organizationId,
                          organizationName: org.organizationName,
                          regionId: org.regionId,
                          verticalId: org.verticalId,
                        },
                      });
                    }}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Assess
                  </Button>

                  {/* Viewing mirrors the backend's GetViewableAssessmentAsync: org read or assignment. */}
                  {org.hasAssessment && org.latestAssessment?.ransomwareAssessmentID && org.latestAssessment?.ransomwareStatus === "completed" &&
                    (canViewOrganizationAssessments() || canAssessViaAssignment(org.latestAssessment?.ransomwareAssignedToUserId, org.latestAssessment?.assigneeUserIds || [])) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/RansomwareAssessment/${org.latestAssessment.ransomwareAssessmentID}?step=review`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Answers
                    </Button>
                  )}

                  {org.hasAssessment && org.latestAssessment?.ransomwareAssessmentID && org.latestAssessment?.ransomwareStatus === "completed" ? (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/RansomwareAssessment/${org.latestAssessment.ransomwareAssessmentID}/risks`);
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({org.activeRisks ?? org.totalRisks})
                    </Button>
                  ) : (org.totalRisks ?? 0) > 0 ? (
                    // No org assessment, but org-level (tagged) Ransomware risks exist
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/risks?types=Ransomware&scope=org`);
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({org.activeRisks ?? org.totalRisks})
                    </Button>
                  ) : (
                    <Button size="sm" variant="primary" className="px-1 py-1 h-9 text-xs" disabled title="No org-level Ransomware risks">
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks (0)
                    </Button>
                  )}

                  {canManageOrganizationRisks() && org.latestAssessment?.ransomwareAssessmentID && (
                    <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                      disabled={org.latestAssessment?.ransomwareStatus !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToReassess(org.latestAssessment.ransomwareAssessmentID);
                        setShowReassessModal(true);
                      }}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reassess
                    </Button>
                  )}

                  {canDeleteAssessment && org.latestAssessment?.ransomwareAssessmentID && (
                    <Button size="sm" variant="danger" className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(org.latestAssessment.ransomwareAssessmentID); setShowDeleteModal(true); }}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                    </div>
                  </div>

                  {org.latestAssessment?.ransomwareAssessmentID && (
                    <>
                      <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                      <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                        <AssessmentActivityLog assessmentType="ransomware" assessmentId={String(org.latestAssessment.ransomwareAssessmentID)} hideQuestionEvents />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {showAssignModal && selectedEntityForAssign && (
          <OrgAssignAssessModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            organizationId={selectedEntityForAssign.organizationId}
            assessmentType="ransomware"
            hasExistingAssessment={selectedEntityForAssign.hasAssessment}
            refreshData={() => refreshOrgAssessment(selectedEntityForAssign.organizationId)}
          />
        )}

        <AssessmentRespondentsModal
          isOpen={respondentsAssessment != null}
          onClose={() => setRespondentsAssessment(null)}
          assessmentType="ransomware"
          assessmentId={respondentsAssessment?.id}
          refreshData={() => refreshOrgAssessment(respondentsAssessment?.organizationId)}
        />

        <DeleteAssessmentModal isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
          onConfirm={handleDeleteAssessment} isDeleting={deleting} />

        <ReassessModal
          isOpen={showReassessModal}
          onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
          onConfirm={handleReassessAssessment}
          isReassessing={reassessing}
        />

        <AssessmentUnassignedModal
          isOpen={showUnassignedAlert}
          onClose={() => setShowUnassignedAlert(false)}
        />
      </div>
    </div>
  );
};

export default RansomwareOrganizations;
